import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  Paper,
  Typography,
  Grid,
  TextField,
  MenuItem,
  Button,
  Avatar,
  IconButton,
  Divider,
  Chip,
} from "@mui/material";
import PhotoCamera from "@mui/icons-material/PhotoCamera";
import EditIcon from "@mui/icons-material/Edit";
import { useToast } from "../../components/Toast";
import { useFirebaseAuth } from "../../hooks/useFirebaseAuth";
import axios from "axios";
import { Loading } from "../../components/Loading";
import { uploadFile } from "../../utils/UploadFile";
import { fetchDocuments } from "../../utils/FetchDocuments";
import { formatDate } from "../../utils/utils";
import { commonButtonProps, commonTextFieldProps } from "../../utils/styles";
import { NotRegisteredOverlay } from "./NotRegisteredOverlay";
import { HelperPopup } from "./HelperPopup";
import { getValidToken } from "../../utils/authUtils";
import { OperationType } from "../../types/operationType";
import { usePlanStatus } from "../../hooks/usePlanStatus";
import { auth } from "../../firebase/config";
import ActivationDialog from "./ActivationDialog";

interface CompanyProfile {
  id: string;
  name: string;
  gstNumber: string;
  address: string;
  city: string;
  state: string;
  pincode: string;
  primaryContact: string;
  primaryPhone: string;
  alternatePhone: string;
  email: string;
  operationType: OperationType;
  fleetSize: number;
  status: "ACTIVE" | "INACTIVE";
  verificationStatus: "PENDING" | "VERIFIED" | "REJECTED";
  planType: string;
  planExpiry: string;
  logo: string;
  createdAt: string;
  updatedAt: string;
  deletedAt: string | null;
}

const CompanyProfile: React.FC = () => {
  const { showToast } = useToast();
  const { user, loading: authLoading } = useFirebaseAuth();
  const [isLoading, setIsLoading] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const [logoPreview, setLogoPreview] = useState<string | null>(null);
  const [selectedLogoFile, setSelectedLogoFile] = useState<File | null>(null);
  const [logoUrl, setLogoUrl] = useState<string>("");
  const [logoId, setLogoId] = useState<string>("");
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [profile, setProfile] = useState<CompanyProfile>({
    id: "",
    name: "",
    gstNumber: "",
    address: "",
    city: "",
    state: "",
    pincode: "",
    primaryContact: "",
    primaryPhone: "",
    alternatePhone: "",
    email: "",
    operationType: OperationType.ALL,
    fleetSize: 0,
    status: "ACTIVE",
    verificationStatus: "PENDING",
    planType: "",
    planExpiry: "",
    logo: "",
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
    deletedAt: null,
  });
  const [isNotRegistered, setIsNotRegistered] = useState(false);
  const [isActivationPending, setIsActivationPending] = useState(false);
  const [showHelperPopup, setShowHelperPopup] = useState(false);
  const {
    invalidatePlanStatus,
    planStatus,
    loading: planStatusLoading,
  } = usePlanStatus();
  const fetchCompanyProfile = async () => {
    try {
      // wait for plan status to be loaded
      if (!planStatus?.companyId) {
        setIsNotRegistered(true);
        return;
      } else if (planStatus?.isNewUnpaidUser) {
        // is new user with activation pending
        setIsActivationPending(true);
        return;
      } else if (planStatus?.company?.vehicleCount === 0) {
        setShowHelperPopup(true);
        // is new user with account setup pending
      } else {
        // is new user with account setup complete
        await fetchCompanyLogo();
      }

      const token = await getValidToken();
      const url = `${process.env.REACT_APP_API_HOST}company/${planStatus?.companyId}`;
      const response = await axios.get(url, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.data?.isSuccess) {
        setProfile(response.data.data);
      } else {
        return null;
      }
    } catch (error) {
      auth.signOut();
      return null;
    } finally {
      setIsLoading(false);
    }
  };

  const fetchCompanyLogo = async () => {
    try {
      if (!planStatus?.companyId) return;

      const documents = await fetchDocuments({
        documentContext: "company",
        documentTypeId: planStatus?.companyId,
        documentType: "company-profile",
      });

      if (documents && documents.length > 0) {
        setLogoUrl(documents[0].presignedUrl);
        setLogoId(documents[0].id);
      }
    } catch (error) {
      return null;
    }
  };

  useEffect(() => {
    if (user && !authLoading && !planStatusLoading) {
      fetchCompanyProfile();
    }
  }, [user, authLoading, planStatusLoading]);

  const handleChange =
    (field: keyof CompanyProfile) =>
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const value = event.target.value;

      // Simple number validation for phone fields
      if (field === "primaryPhone" || field === "alternatePhone") {
        if (!/^\d*$/.test(value)) {
          return; // Ignore non-numeric input
        }
      }

      setProfile((prev) => ({
        ...prev,
        [field]: value,
      }));
    };

  const handleLogoChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setLogoPreview(reader.result as string);
      };
      reader.readAsDataURL(file);
      setSelectedLogoFile(file);
    }
  };

  const handleLogoRemove = () => {
    if (logoPreview) {
      URL.revokeObjectURL(logoPreview);
    }
    setLogoPreview(null);
    setSelectedLogoFile(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    try {
      const token = await getValidToken();
      const localUser = sessionStorage.getItem("user");
      const companyId = localUser ? JSON.parse(localUser).companyId : null;
      if (!companyId || !user) {
        showToast("Error updating profile", "error");
        return;
      }

      let uploadedDocument;
      // Upload logo if a new one was selected
      if (selectedLogoFile) {
        try {
          uploadedDocument = await uploadFile({
            file: selectedLogoFile,
            documentType: "company-profile",
            documentTypeId: companyId,
            documentContext: "company",
            description: "Company logo",
            companyId: companyId,
            ...(logoId && {
              documentId: logoId,
              isUpdate: true,
            }),
          });
        } catch (error) {
          showToast("Failed to upload logo", "error");
          return;
        }
      }

      const url = `${process.env.REACT_APP_API_HOST}company/${companyId}`;
      const response = await axios.put(
        url,
        { ...profile, logo: uploadedDocument?.id || profile.logo },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.data?.isSuccess) {
        showToast("Profile updated successfully", "success");
        handleInvalidate();
        setIsEditing(false);
        // Clean up preview URL
        if (logoPreview) {
          URL.revokeObjectURL(logoPreview);
        }
        setLogoPreview(null);
        setSelectedLogoFile(null);
        await fetchCompanyProfile();
        await fetchCompanyLogo(); // Refresh the logo
      } else {
        showToast("Failed to update profile", "error");
      }
    } catch (error) {
      showToast("Failed to update profile", "error");
    }
  };
  const handleInvalidate = () => {
    invalidatePlanStatus();
  };
  // Clean up preview URL when component unmounts
  useEffect(() => {
    return () => {
      if (logoPreview) {
        URL.revokeObjectURL(logoPreview);
      }
    };
  }, [logoPreview]);

  if (isLoading || authLoading) {
    return <Loading message="Loading company profile..." fullScreen={true} />;
  }

  const renderField = (
    label: string,
    value: string | number,
    field: keyof CompanyProfile
  ) => {
    if (isEditing) {
      return (
        <TextField
          fullWidth
          label={label}
          value={value}
          onChange={handleChange(field)}
          inputProps={{
            maxLength:
              field === "primaryPhone" || field === "alternatePhone"
                ? 10
                : undefined,
            type:
              field === "primaryPhone" || field === "alternatePhone"
                ? "tel"
                : undefined,
          }}
          {...commonTextFieldProps}
        />
      );
    }
    return (
      <Box>
        <Typography
          variant="subtitle2"
          color="text.secondary"
          sx={{ fontSize: "0.75rem" }}
        >
          {label}
        </Typography>
        <Typography variant="body1" sx={{ fontSize: "0.75rem" }}>
          {value || "Not specified"}
        </Typography>
      </Box>
    );
  };

  return (
    <>
      {isNotRegistered ? (
        <NotRegisteredOverlay />
      ) : isActivationPending ? (
        <ActivationDialog price={499} onClose={() => {}} />
      ) : (
        <>
          {/* Show overlay if not registered */}

          <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
            <Typography
              variant="h4"
              component="h1"
              sx={{ flexGrow: 1, fontSize: "1rem" }}
            >
              Company Profile
            </Typography>

            {!isEditing && planStatus?.permissions?.canEditCompany && (
              <Button
                variant="contained"
                color="primary"
                startIcon={<EditIcon />}
                onClick={() => setIsEditing(true)}
                {...commonButtonProps}
              >
                Edit Profile
              </Button>
            )}
          </Box>
          {/* Show onboarding helper popup if no vehicles and user hasn't dismissed it */}
          {showHelperPopup && (
            <HelperPopup
              title="Welcome to Gaadi Kharcha!"
              description="Congratulations on setting up your company profile! Let's get you started with your first vehicle."
              open={true}
              onClose={() => setShowHelperPopup(false)}
            />
          )}
          <Box component="form" noValidate>
            <Grid container spacing={1.5}>
              {/* Logo and Status Section */}
              <Grid item xs={12}>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    mb: 1,
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center" }}>
                    {!(logoPreview || logoUrl) ? (
                      <Avatar
                        src={logoPreview || logoUrl || ""}
                        sx={{
                          width: 80,
                          height: 80,
                          mr: 2,
                          border: "2px solid #e0e0e0",
                        }}
                      />
                    ) : (
                      <img
                        src={logoPreview || logoUrl || ""}
                        style={{
                          height: 80,
                          width: "auto",
                        }}
                      />
                    )}
                    {isEditing && (
                      <Box>
                        <input
                          accept="image/*"
                          style={{ display: "none" }}
                          id="logo-upload"
                          type="file"
                          onChange={handleLogoChange}
                          ref={fileInputRef}
                        />
                        <label htmlFor="logo-upload">
                          <IconButton
                            color="primary"
                            aria-label="upload company logo"
                            component="span"
                            size="small"
                          >
                            <PhotoCamera />
                          </IconButton>
                        </label>
                        {logoPreview && (
                          <Button
                            size="small"
                            color="error"
                            onClick={handleLogoRemove}
                            sx={{ ml: 1 }}
                          >
                            Remove
                          </Button>
                        )}
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          display="block"
                        >
                          {logoPreview ? "Change logo" : "Upload logo"}
                        </Typography>
                      </Box>
                    )}
                  </Box>

                  {/* Status and Plan Info */}
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      gap: 0.5,
                      alignItems: "flex-end",
                    }}
                  >
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Typography variant="caption" color="text.secondary">
                        Plan:
                      </Typography>
                      <Chip
                        label={profile.planType || "No Plan"}
                        color="primary"
                        variant="outlined"
                        size="small"
                        sx={{ fontSize: "0.7rem", height: 20 }}
                      />
                    </Box>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Typography variant="caption" color="text.secondary">
                        Status:
                      </Typography>
                      <Typography
                        variant="caption"
                        color={
                          profile.status === "ACTIVE"
                            ? "success.main"
                            : "error.main"
                        }
                        sx={{ fontWeight: "medium" }}
                      >
                        {profile.status || "Not specified"}
                      </Typography>
                    </Box>
                    {profile.planType === "FREE TRIAL" && (
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 1 }}
                      >
                        <Typography variant="caption" color="text.secondary">
                          Expires:
                        </Typography>
                        <Typography variant="caption" color="warning.main">
                          {formatDate(profile.planExpiry) || "Not specified"}
                        </Typography>
                      </Box>
                    )}
                  </Box>
                </Box>
              </Grid>

              <Grid item xs={12}>
                <Divider sx={{ my: 1 }} />
              </Grid>

              {/* Company Details - More compact layout */}
              <Grid item xs={12} md={6}>
                {renderField("Company Name", profile.name, "name")}
              </Grid>
              <Grid item xs={12} md={6}>
                {renderField("GST Number", profile.gstNumber, "gstNumber")}
              </Grid>
              <Grid item xs={12} md={6}>
                {renderField("Address", profile.address, "address")}
              </Grid>
              <Grid item xs={12} md={6}>
                <Grid container spacing={1}>
                  <Grid item xs={4}>
                    {renderField("City", profile.city, "city")}
                  </Grid>
                  <Grid item xs={4}>
                    {renderField("State", profile.state, "state")}
                  </Grid>
                  <Grid item xs={4}>
                    {renderField("Pincode", profile.pincode, "pincode")}
                  </Grid>
                </Grid>
              </Grid>

              <Grid item xs={12} md={6}>
                {isEditing ? (
                  <TextField
                    fullWidth
                    select
                    label="Operation Type"
                    value={profile.operationType}
                    onChange={handleChange("operationType")}
                    {...commonTextFieldProps}
                    disabled={true}
                  >
                    {Object.values(OperationType).map((type) => (
                      <MenuItem
                        key={type}
                        value={type}
                        sx={{ fontSize: "0.75rem" }}
                      >
                        {type}
                      </MenuItem>
                    ))}
                  </TextField>
                ) : (
                  renderField(
                    "Operation Type",
                    profile.operationType,
                    "operationType"
                  )
                )}
              </Grid>
              <Grid item xs={12} md={6}>
                {renderField("Fleet Size", profile.fleetSize, "fleetSize")}
              </Grid>

              <Grid item xs={12}>
                <Divider sx={{ my: 1 }} />
              </Grid>

              {/* Contact Details - Compact layout */}
              <Grid item xs={12} sm={6} md={3}>
                {renderField(
                  "Primary Contact",
                  profile.primaryContact,
                  "primaryContact"
                )}
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                {renderField(
                  "Primary Phone",
                  profile.primaryPhone,
                  "primaryPhone"
                )}
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                {renderField(
                  "Alternate Phone",
                  profile.alternatePhone,
                  "alternatePhone"
                )}
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                {renderField("Email", profile.email, "email")}
              </Grid>
              <Grid item xs={12}>
                <Divider sx={{ my: 1 }} />
              </Grid>
              {isEditing && (
                <Grid
                  item
                  xs={12}
                  sx={{
                    display: "flex",
                    justifyContent: "flex-end",
                    gap: 1,
                    mt: 1,
                  }}
                >
                  <Button
                    variant="outlined"
                    color="error"
                    onClick={() => {
                      fetchCompanyProfile();
                      setIsEditing(false);
                    }}
                    {...commonButtonProps}
                  >
                    Cancel
                  </Button>
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={(e) => handleSubmit(e)}
                    {...commonButtonProps}
                  >
                    Save Changes
                  </Button>
                </Grid>
              )}
            </Grid>
          </Box>
        </>
      )}
    </>
  );
};

export default CompanyProfile;
